import React, {ReactNode, useMemo} from 'react';

import {useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    CommonGate,
    filtersChanged,
    filtersReset,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/virtualWarehouse/grid/table';
import {VirtualWarehouseGridFormattedRow, VirtualWarehouseGridRawRow} from 'types';

interface Props {
    configFunc: (t: TFunction, brands: any[], groups: any[]) => Array<any>;
    dataMapper: (
        data: VirtualWarehouseGridRawRow[] | undefined,
        t: TFunction,
    ) => VirtualWarehouseGridFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const VirtualWarehouseGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
}: Props) => {
    const {t} = useTranslation();
    const {data, pageNumber, filters, sortings, dataLoading} = useStore(tableState$);
    const brandOptions = data?.brands?.map((b) => ({label: b, value: b})) || [];
    const groupOptions = data?.groups?.map((b) => ({label: b, value: b})) || [];
    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t, brandOptions, groupOptions),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings, brandOptions, groupOptions],
    );
    const mappedData = React.useMemo(() => dataMapper(data?.content, t), [dataMapper, data, t]);

    return (
        <>
            <CommonGate />
            <Table
                columns={columns}
                data={mappedData}
                // TotalCount from backed - the remaining number of elements to the end of the list
                // totalRowsCount={data?.totalCount}
                pageIndex={pageNumber}
                onPageChange={pageNumberChanged}
                hasNext={data?.hasNext}
                isLoading={dataLoading}
                headerControls={headerControls}
                userConfig={{
                    gridName: 'virtualWarehouse',
                    saveConfig: saveGridConfig,
                    getConfig: fetchGridConfig,
                }}
                exportConfig={exportConfig}
            />
        </>
    );
};
